import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enAU from 'date-fns/locale/en-AU';
import { useAtomValue } from 'jotai';
import { lazy, ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { IS_SIGNED_IN } from './store/auth';
import { LIVE_LAST_EVENTS, LIVE_TIME } from './store/liveEvents';
import { AppProvider } from './util/AppProvider';
import { DevTools } from './util/DevTools';
import { SignIn } from './Authn/SignIn';
import { Loading } from './Common/Loading';
import { MapDataProvider, MapDataReplayProvider } from './Components/Map/useMapData';
import { BANNER_HEIGHT } from './Components/Map/SidebarAndMap/SidebarAndMap';
import { PYBAR_URL } from './store/url';
import { InitialLocationProvider } from './util/InitialLocationProvider';
import { EditorMapProvider } from './Components/Map/Editor/useEditorMap';
import { HeatMapProvider } from './Components/Map/HeatMap/useHeatMap';
import { LiveReplayMapProvider } from './Components/Map/LiveAndReplay/useLiveReplayMap';
import { MapType } from './util/enums';
import NavigationTabs from './Components/Navigation/NavigationTabs';

const Deferred = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const AcceptInvite = lazy(() => import('./Authn/AcceptInvite'));
const AuthError = lazy(() => import('./Authn/AuthError'));
const Authed = lazy(() => import('./Authn/AuthedRoute'));
const ProjectSelection = lazy(() => import('./Authn/ProjectSelection'));
const AccountRecovery = lazy(() => import('./Authn/AccountRecovery'));
const LiveEventsProvider = lazy(() => import('./util/LiveEventsProvider'));
const LatestEventsProvider = lazy(() => import('./util/LatestEventsProvider'));
const ReplayEventsProvider = lazy(() => import('./util/ReplayEventsProvider'));
const LiveMap = lazy(() => import('./Components/Map/LiveAndReplay/LiveMap'));
const ReplayMap = lazy(() => import('./Components/Map/LiveAndReplay/ReplayMap'));
const Editor = lazy(() => import('./Components/Map/Editor/EditorMap'));
const UserAccount = lazy(() => import('./Components/UserAccount/UserAccount'));
const SuperAdmin = lazy(() => import('./Components/SuperAdmin/SuperAdmin'));
const ProjectConfig = lazy(() => import('./Components/ProjectConfig/ProjectConfig'));
const HeatMap = lazy(() => import('./Components/Map/HeatMap/HeatMap'));
const Beacons = lazy(() => import('./Components/Assets/Beacons/Beacons'));
const Devices = lazy(() => import('./Components/Assets/Devices/Devices'));
const GPSTrackers = lazy(() => import('./Components/Assets/GPSTrackers/GPSTrackers'));
const MQTTGateways = lazy(() => import('./Components/Assets/MQTTGateways/MQTTGateways'));
const Tools = lazy(() => import('./Components/Assets/Tools/Tools'));
const Events = lazy(() => import('./Components/Events/Events'));

export const App = () => {
  return (
    <AppProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
        <BrowserRouter>
          <Routes>
            <Route path="/sign-in" element={<SignIn />} />
            <Route
              path="/my-account"
              element={
                <Deferred>
                  <Authed>
                    <UserAccount />
                  </Authed>
                </Deferred>
              }
            />
            {[
              {
                path: 'accept-invite',
                element: <AcceptInvite />,
              },
              {
                path: 'auth/error',
                element: <AuthError />,
              },
              {
                path: 'project-selection',
                element: <ProjectSelection />,
              },
              {
                path: 'account-recovery',
                element: <AccountRecovery />,
              },
              {
                path: '/underground',
                element: (
                  <>
                    <NavigationTabs />
                    <iframe
                      style={{
                        border: 'none',
                        width: '100%',
                        height: `calc(100vh - ${BANNER_HEIGHT}px)`,
                      }}
                      src={useAtomValue(PYBAR_URL)}
                    ></iframe>
                  </>
                ),
              },
            ].map(({ path, element }) => (
              <Route key={path} path={`/${path}`} element={<Deferred>{element}</Deferred>} />
            ))}

            {[
              {
                path: 'editor-map',
                element: (
                  <EditorMapProvider>
                    <Editor />
                  </EditorMapProvider>
                ),
              },
              {
                path: 'live-map',
                mapType: 'live' as const,
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <LiveReplayMapProvider mapType={MapType.LIVE_MAP}>
                          <LiveMap />
                        </LiveReplayMapProvider>
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'replay-map',
                mapType: 'replay' as const,
                element: (
                  <ReplayEventsProvider>
                    <MapDataReplayProvider>
                      <LiveReplayMapProvider mapType={MapType.REPLAY_MAP}>
                        <ReplayMap />
                      </LiveReplayMapProvider>
                    </MapDataReplayProvider>
                  </ReplayEventsProvider>
                ),
              },
              {
                path: 'heat-map',
                element: (
                  <HeatMapProvider>
                    <HeatMap />
                  </HeatMapProvider>
                ),
              },
            ].map(({ path, element, mapType }) => (
              <Route
                key={path}
                path={`/${path}`}
                element={
                  <Deferred>
                    <Authed mapType={mapType}>
                      <InitialLocationProvider>{element}</InitialLocationProvider>
                    </Authed>
                  </Deferred>
                }
              />
            ))}

            {[
              {
                path: 'project-config',
                element: <ProjectConfig />,
              },
              {
                path: 'super-admin',
                element: <SuperAdmin />,
              },
              {
                path: 'beacons',
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <Beacons />
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'devices',
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <Devices />
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'gps-trackers',
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <GPSTrackers />
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'mqtt-gateways',
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <MQTTGateways />
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'tools',
                element: (
                  <LiveEventsProvider>
                    <LatestEventsProvider
                      hoursAgo={2}
                      timeNowAtom={LIVE_TIME}
                      latestEventsAtom={LIVE_LAST_EVENTS}
                    >
                      <MapDataProvider>
                        <Tools />
                      </MapDataProvider>
                    </LatestEventsProvider>
                  </LiveEventsProvider>
                ),
              },
              {
                path: 'events',
                element: <Events />,
              },
            ].map(({ path, element }) => (
              <Route
                key={path}
                path={`/${path}`}
                element={
                  <Deferred>
                    <Authed>{element}</Authed>
                  </Deferred>
                }
              />
            ))}
            <Route
              path="*"
              element={
                useAtomValue(IS_SIGNED_IN) ? (
                  <Navigate to="/project-selection" />
                ) : (
                  <Navigate to="/sign-in" />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </LocalizationProvider>
    </AppProvider>
  );
};

export const DebugApp = () => (
  <DevTools>
    <App />
  </DevTools>
);
